// src/app/router.jsx
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import AppShell from '@app/AppShell';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@pages/auth/Login'));
const ActivatePage = lazy(() => import('@pages/auth/Activate'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const HomePage = lazy(() => import('@pages/HomePage'));
const VendorsListPage = lazy(() => import('@pages/vendors/VendorsListPage'));
const EmployeesListPage = lazy(() => import('@pages/employees/EmployeeListPage'));
const EmployeeImportPage = lazy(() => import('@pages/employees/EmployeeImportPage'));
const HotelSearchPage = lazy(() => import('@pages/bookings/HotelSearchPage'));
const HotelDetailPage = lazy(() => import('@pages/bookings/HotelDetailPage'));
const ChangeBookingPage = lazy(() => import('@pages/bookings/ChangeBookingPage'));
const BookingGuestDetailsPage = lazy(() => import('@pages/bookings/BookingGuestDetailsPage'));
const BookingConfirmationPage = lazy(() => import('@pages/bookings/BookingConfirmationPage'));
const BookingFinalConfirmationPage = lazy(() => import('@pages/bookings/BookingFinalConfirmationPage'));
const GroupConfirmationPage = lazy(() => import('@pages/bookings/GroupConfirmationPage'));
const PaymentStepRedirect = lazy(() => import('@features/bookings/components/PaymentStepRedirect'));
const MyReservationsPage = lazy(() => import('@pages/bookings/MyReservationsPage'));
const CustomerFeesPage = lazy(() => import('@pages/fees/CustomerFeesPage'));
const CustomersPage = lazy(() => import('@pages/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@pages/customers/CustomerDetailPage'));
const ChainsPage = lazy(() => import('@pages/chains/ChainsPage'));
const ProfilePage = lazy(() => import('@pages/users/ProfilePage'));
const UserManagement = lazy(() => import('../features/users/components/UserManagement'));
const BillingPage = lazy(() => import('@pages/billing/BillingPage'));
const BillingBatchDetailPage = lazy(() => import('@pages/billing/BillingBatchDetailPage'));
const BillingBatchesPage = lazy(() => import('@pages/billing/BillingBatchesPage'));
const HotelChargesPage = lazy(() => import('@pages/billing/HotelChargesPage'));
const StayLedgerPage = lazy(() => import('@pages/billing/StayLedgerPage'));
const ReportsShell = lazy(() => import('@pages/reports/ReportsShell'));
const LodgingAnalysisReportPage = lazy(() => import('@pages/reports/LodgingAnalysisReportPage'));
const InvoiceReportPage = lazy(() => import('@pages/reports/InvoiceReportPage'));
const RecordPaymentPage = lazy(() => import('@pages/billing/RecordPaymentPage'));
const RecordMultipleBatchPaymentPage = lazy(() => import('@pages/billing/RecordMultipleBatchPaymentPage'));
const PaymentExceptionsPage = lazy(() => import('@pages/billing/PaymentExceptionsPage'));
const PaymentsPage = lazy(() => import('@pages/billing/PaymentsPage'));
const StaffReservationsPage = lazy(() => import('@pages/staff-reservations/StaffReservationsPage'));
const CreateManualReservationPage = lazy(() => import('@pages/staff-reservations/CreateManualReservationPage'));
const VccCardsListPage = lazy(() => import('@pages/vcc-cards/VccCardsListPage'));
const IntegrationsPage = lazy(() => import('@pages/integrations/IntegrationsPage'));
const ErrorLogsPage = lazy(() => import('@pages/admin/ErrorLogsPage'));
const AuditLogPage = lazy(() => import('@pages/admin/AuditLogPage'));
const ErrorLogDetailPage = lazy(() => import('@pages/admin/ErrorLogDetailPage'));
const SeederPage = lazy(() => import('@pages/admin/SeederPage'));
const PoliciesPage = lazy(() => import('@pages/policies/PoliciesPage'));
const TagsPage = lazy(() => import('@pages/admin/TagsPage'));
const TagDetailPage = lazy(() => import('@pages/admin/TagDetailPage'));
const PolicyReviewsPage = lazy(() => import('@pages/policy-reviews/PolicyReviewsPage'));
import AuthProvider, { useAuth, useCan } from '@app/providers/AuthProvider';
import { PERM } from '@shared/constants/permissions';
import { SelectedRatesProvider } from '@features/bookings/context/SelectedRatesContext';
import RouteErrorBoundary from '@shared/components/feedback/RouteErrorBoundary';

const PageLoader = () => <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;

// Boundary OUTSIDE Suspense so it catches both a render throw and a failed
// dynamic import. Inside, a rejected lazy() would escape past it.
const withSuspense = (Component) => (
  <RouteErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
);

// --- wrappers ---
function WithAuthProvider() {
  // NotificationProvider used to live here; it now mounts at the root in
  // main.jsx so it's available to boot-time errors too (login page,
  // password-reset page, axios interceptor at the very first request).
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

function RequirePermission({ ability }) {
  return useCan(ability) ? <Outlet /> : <Navigate to="/user-home" replace />;
}

/** An old Reservation Ledger link, sent to the stay's page (2026-09-29). */
function RedirectToStay() {
  const { reservationId } = useParams();
  return <Navigate to={`/billing/stays/${reservationId}`} replace />;
}

// The old modify entry sent a traveller back through the hotel page to pick a
// rate again. Changes happen on the Change booking page now (ADR-0035); a
// saved link or browser history entry still lands somewhere that works.
function RedirectToChangeBooking() {
  const { confirmationId } = useParams();
  return <Navigate to={`/reservations/${encodeURIComponent(confirmationId)}/change`} replace />;
}

// Role-based gate, for a route whose subject is the role itself — the Super
// Admin-only seeder page. Anything that is an operation asks for a permission
// with RequirePermission instead (ADR-0038).
function RequireRole({ roles }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) => roles.includes(r?.name));
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

function Protected() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

/**
 * The mirror of Protected: routes that only make sense when signed OUT.
 *
 * A session belongs to the browser, so opening /login in a second tab while
 * already signed in used to render the sign-in form — the app asking who you
 * are when it already knows. Worse than pointless: submitting it runs
 * Auth::login() + session()->regenerate() over the live session, which is also
 * the path by which one tab silently swaps the identity every other tab is
 * using.
 *
 * Standard equipment elsewhere — Laravel's `guest` middleware, Rails'
 * redirect_if_authenticated. This codebase had Protected and never its
 * counterpart.
 *
 * Signing out is unaffected: signOut empties the session before navigating, so
 * /login renders normally.
 */
function GuestOnly() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Navigate to="/user-home" replace /> : <Outlet />;
}

function SelectedRatesProviderOutlet() {
  return (
    <SelectedRatesProvider>
      <Outlet />
    </SelectedRatesProvider>
  );
}

// --- router ---
const routes = [
  {
    element: <WithAuthProvider />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: '/login', element: withSuspense(SignInPage) },
        ],
      },
      // Deliberately left reachable while signed in. Both are followed from a
      // link in an email, and both carry a token that identifies the account
      // the link is for — which need not be the account currently signed in on
      // this browser. Bouncing a signed-in user to /user-home would strand
      // anyone activating a second account or resetting a password from a
      // machine where someone else is logged in.
      { path: '/activate', element: withSuspense(ActivatePage) },
      { path: '/reset-password', element: withSuspense(ResetPasswordPage) },

      {
        element: <Protected />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              {
                path: 'user-home',
                element: withSuspense(HomePage),
                handle: { crumb: 'Home', title: 'Home' },
              },
              {
                path: 'profile',
                element: withSuspense(ProfilePage),
                handle: { crumb: 'Profile', title: 'Profile Overview' },
              },
              {
                // Your reservations: `view own reservations`, what the list's
                // API checks — a Vendor holds it without `book reservations`
                // and was sent home (2026-10-08). Changing one books, so the
                // change page keeps `book reservations`.
                element: <RequirePermission ability={PERM.VIEW_OWN_RESERVATIONS} />,
                children: [
                  {
                    path: 'reservations',
                    element: withSuspense(MyReservationsPage),
                    // hideCrumbs: page has its own sticky search+tabs header,
                    // and the default breadcrumbs row was pushing that header
                    // ~100px below the viewport top ("floating in the middle").
                    // See shared/components/layout/PageBreadcrumbs.jsx.
                    handle: { crumb: 'Reservations', title: 'My Reservations', hideCrumbs: true },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.BOOK_RESERVATIONS} />,
                children: [
                  {
                    // Change a booking's dates — same room, same rate (ADR-0035).
                    path: 'reservations/:confirmationId/change',
                    element: withSuspense(ChangeBookingPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservations', to: '/reservations' },
                        { label: 'Change booking' },
                      ],
                      title: 'Change booking',
                    },
                  },
                ],
              },
       
              {
                element: <RequirePermission ability={PERM.VIEW_USERS} />,
                children: [
                  {
                    // Every customer's logins — the same pair the API checks.
                    element: <RequirePermission ability={PERM.VIEW_ALL_CUSTOMERS} />,
                    children: [
                      {
                        path: 'users',
                        element: withSuspense(UserManagement),
                        handle: { crumb: 'User Management', title: 'User Management' },
                      },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.MANAGE_HOTEL_CHAINS} />,
                children: [
                  {
                    path: 'chains',
                    element: withSuspense(ChainsPage),
                    handle: { crumb: 'Hotel Chains', title: 'Hotel Chains' },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_VENDORS} />,
                children: [
                  {
                    path: 'vendors',
                    element: withSuspense(VendorsListPage),
                    handle: { crumb: 'Vendors', title: 'Vendors' },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_CUSTOMERS} />,
                children: [
                  {
                    path: 'customers',
                    element: withSuspense(CustomersPage),
                    handle: { crumb: 'Customers', title: 'Customers' },
                  },
                  {
                    path: 'customers/:id',
                    element: withSuspense(CustomerDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Customers', to: '/customers' },
                        { label: 'Customer' },
                      ],
                      title: 'Customer',
                    },
                  },
                ],
              },
              // /vendors/create route removed — Add Vendor now opens
              // VendorFormDialog inline from the list page. Edit stays
              // as a full page at /vendors/:id/edit below.
              {
                element: <RequirePermission ability={PERM.EDIT_VENDORS} />,
                children: [
                  {
                    // The Derby Status page was folded into Vendors
                    // (2026-10-22): its column, filter and on/off switch
                    // live there. Old links and bookmarks land on the
                    // DerbySoft tab.
                    path: 'vendors/derby-status',
                    element: <Navigate to="/vendors?source=derbysoft" replace />,
                  },
                  // /vendors/:id/edit route retired 2026-08-10. Edit
                  // now opens VendorFormDialog inline from the list
                  // page — same modal Create uses. See
                  // features/vendors/components/VendorFormDialog.jsx.
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_TRANSACTIONS} />,
                children: [
                  // The /billing root is the MSI "generate batch from
                  // candidates" dashboard — calls /api/billing/candidates
                  // which requires `manage billing`. Block TMs at the route
                  // level so they don't hit the 403 toast.
                  {
                    element: <RequirePermission ability={PERM.MANAGE_BILLING} />,
                    children: [
                      {
                        path: 'billing',
                        element: withSuspense(BillingPage),
                        handle: { crumb: 'Billing', title: 'Billing Batches' },
                      },
                    ],
                  },
                  {
                    path: 'billing/batches',
                    element: withSuspense(BillingBatchesPage),
                    handle: { crumb: 'Batches', title: 'All Batches' },
                  },
                  {
                    path: 'billing/payments',
                    element: withSuspense(PaymentsPage),
                    handle: { crumb: 'Payment History', title: 'Payment History' },
                  },
                  {
                    path: 'reports',
                    element: withSuspense(ReportsShell),
                    handle: { crumb: 'Reports', title: 'Reports' },
                    children: [
                      { index: true, element: <Navigate to="/reports/lodging-analysis" replace /> },
                      {
                        path: 'lodging-analysis',
                        element: withSuspense(LodgingAnalysisReportPage),
                        handle: { crumb: 'Lodging Analysis', title: 'Lodging Analysis Report' },
                      },
                      {
                        path: 'invoice',
                        element: withSuspense(InvoiceReportPage),
                        handle: { crumb: 'Invoice Report', title: 'Invoice Report' },
                      },
                    ],
                  },
                  {
                    path: 'billing/:id',
                    element: withSuspense(BillingBatchDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Billing', to: '/billing' },
                        { label: 'Batch Detail' },
                      ],
                      title: 'Billing Batch',
                    },
                  },
                  // MSI-only billing writes — recording payments. Travel
                  // Managers navigating to these URLs are redirected to
                  // /user-home. Backend also blocks via the `manage billing`
                  // permission; this is the client-side equivalent.
                  {
                    element: <RequirePermission ability={PERM.MANAGE_BILLING} />,
                    children: [
                      {
                        path: 'billing/record-payment',
                        element: withSuspense(RecordPaymentPage),
                        handle: { crumb: 'Record Payment', title: 'Record Payment' },
                      },
                      {
                        path: 'billing/record-payment/process',
                        element: withSuspense(RecordMultipleBatchPaymentPage),
                        handle: { crumb: 'Process Payment', title: 'Process Payment' },
                      },
                    ],
                  },
                  // Hotel Charges and its stays: every customer's money, read
                  // only. The same pair the API checks (ADR-0038).
                  {
                    element: <RequirePermission ability={PERM.VIEW_ALL_CUSTOMERS} />,
                    children: [
                      {
                        // Every hotel charge and where its money is (billing ADR-025).
                        path: 'billing/charges',
                        element: withSuspense(HotelChargesPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Hotel Charges' },
                          ],
                          title: 'Hotel Charges',
                        },
                      },
                      {
                        path: 'billing/stays/:reservationId',
                        element: withSuspense(StayLedgerPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Hotel Charges', to: '/billing/charges?view=stays' },
                            { label: 'Stay' },
                          ],
                          title: 'Stay',
                        },
                      },
                      // Billed Reservations became Hotel Charges' "By stay" view
                      // on 2026-09-29; old links and bookmarks land there.
                      { path: 'billing/billed-reservations', element: <Navigate to="/billing/charges?view=stays" replace /> },
                      { path: 'billing/billed-reservations/:reservationId', element: <RedirectToStay /> },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_EMPLOYEES} />,
                children: [
                  {
                    path: 'employees',
                    element: withSuspense(EmployeesListPage),
                    handle: { crumb: 'Employees', title: 'Employees' },
                  },
                  {
                    path: 'staff-reservations',
                    element: withSuspense(StaffReservationsPage),
                    handle: { crumb: 'Reservations', title: 'Reservations' },
                  },
                  // Manual reservation create/edit and standalone VCC
                  // issuance each need their own permission (ADR-0038).
                  // Travel Managers see the staff-reservations list, scoped
                  // to their customer, but hold neither. The server checks
                  // the same permissions on every request.
                  {
                    element: <RequirePermission ability={PERM.MANAGE_STAFF_RESERVATIONS} />,
                    children: [
                      {
                        path: 'staff-reservations/create',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Create Reservation', title: 'Create Manual Reservation' },
                      },
                      {
                        path: 'staff-reservations/:id/edit',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Edit Reservation', title: 'Edit Manual Reservation' },
                      },
                    ],
                  },
                  {
                    element: <RequirePermission ability={PERM.MANAGE_VCC_CARDS} />,
                    children: [
                      {
                        path: 'vcc-cards',
                        element: withSuspense(VccCardsListPage),
                        handle: { crumb: 'VCCs', title: 'VCCs' },
                      },
                    ],
                  },
                ],
              },
              {
                // Bulk CSV import is MSI's, like every change to employees
                // (ADR-0043). Backend enforces the same gate via the
                // `bulk import employees` permission on the import routes.
                element: <RequirePermission ability={PERM.BULK_IMPORT_EMPLOYEES} />,
                children: [
                  {
                    path: 'employees/imports',
                    element: withSuspense(EmployeeImportPage),
                    handle: {
                      crumbs: [
                        { label: 'Employees', to: '/employees' },
                        { label: 'Import Employees' },
                      ],
                      title: 'Import Employees',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.MANAGE_CUSTOMER_FEES} />,
                children: [
                  {
                    path: 'customer-fees',
                    element: withSuspense(CustomerFeesPage),
                    handle: { crumb: 'Customer Fees', title: 'Customer Fees' },
                  },
                ],
              },
              {
                // Integrations UI is Admin-only via the narrower `manage
                // integrations` permission. It was previously grouped under
                // `view transactions` alongside payment-exceptions, but that
                // permission is also held by Travel Managers (for billing
                // reads) — grouping them together let TMs into the QuickBooks
                // controls, which is what F-1 in the review flagged.
                element: <RequirePermission ability={PERM.MANAGE_INTEGRATIONS} />,
                children: [
                  {
                    path: 'integrations',
                    element: withSuspense(IntegrationsPage),
                    handle: { crumb: 'Integrations', title: 'Integrations' },
                  },
                  {
                    // Error logs — same permission gate as /integrations
                    // (operational admin data). See
                    // docs/architecture/error-tracking-plan.md.
                    path: 'admin/errors',
                    element: withSuspense(ErrorLogsPage),
                    handle: { crumb: 'Error logs', title: 'Error logs' },
                  },
                  {
                    // The audit log — same gate as Error logs. See
                    // docs/reference/audit-trail.md.
                    path: 'admin/audit-log',
                    element: withSuspense(AuditLogPage),
                    handle: { crumb: 'Audit log', title: 'Audit log' },
                  },
                  {
                    path: 'admin/errors/:id',
                    element: withSuspense(ErrorLogDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Error logs', to: '/admin/errors' },
                        { label: 'Detail' },
                      ],
                      title: 'Error detail',
                    },
                  },
                ],
              },
              {
                // Seeder: Super Admin only. Browser wrapper around
                // billing:seed-bulk artisan command so we don't have to
                // SSH to web17 every time. Backend enforces the same role.
                element: <RequireRole roles={['Super Admin']} />,
                children: [
                  {
                    path: 'admin/seeder',
                    element: withSuspense(SeederPage),
                    handle: { crumb: 'Seeder', title: 'Data Seeder' },
                  },
                ],
              },
              {
                // Resolving an exception writes the billing ledger; Travel
                // Managers read billing but do not manage it (ADR-0038).
                element: <RequirePermission ability={PERM.MANAGE_BILLING} />,
                children: [
                  {
                    path: 'payment-exceptions',
                    element: withSuspense(PaymentExceptionsPage),
                    handle: { crumb: 'Payment Exceptions', title: 'Payment Exceptions' },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_POLICIES} />,
                children: [
                  {
                    path: 'policies',
                    element: withSuspense(PoliciesPage),
                    handle: { crumb: 'Policies', title: 'Policies' },
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_TAGS} />,
                children: [
                  {
                    path: 'tags',
                    element: withSuspense(TagsPage),
                    handle: { crumb: 'Tags', title: 'Tags' },
                  },
                  {
                    // Every customer's records wearing the tag — MSI's, as the
                    // API's attachments listing is.
                    element: <RequirePermission ability={PERM.MANAGE_TAGS} />,
                    children: [
                      {
                        path: 'tags/:id',
                        element: withSuspense(TagDetailPage),
                        handle: { crumb: 'Tag', title: 'Tag detail' },
                      },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission ability={PERM.VIEW_POLICY_REVIEWS} />,
                children: [
                  {
                    path: 'policy-reviews',
                    element: withSuspense(PolicyReviewsPage),
                    handle: { crumb: 'Policy Reviews', title: 'Policy Reviews' },
                  },
                ],
              },
              { index: true, element: <Navigate to="user-home" replace /> },
              {
                // The booking flow: its APIs need `book reservations`, so
                // without it a link landed on pages that 403'd (2026-10-08).
                element: <RequirePermission ability={PERM.BOOK_RESERVATIONS} />,
                children: [
              {
                element: <SelectedRatesProviderOutlet />,
                children: [
                  {
                    path: 'booking',
                    element: withSuspense(HotelSearchPage),
                    handle: { crumb: 'Reservation', title: 'Reservation Search' },
                  },
                  {
                    path: 'booking/:hotelId',
                    element: withSuspense(HotelDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Hotel Details' },
                      ],
                      title: 'Hotel Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/modify/:confirmationId',
                    element: <RedirectToChangeBooking />,
                  },
                  {
                    path: 'booking/confirmation/:confirmationId',
                    element: withSuspense(BookingFinalConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirmation' },
                      ],
                      title: 'Confirmation',
                    },
                  },
                  {
                    path: 'booking/group-confirmation/:groupId',
                    element: withSuspense(GroupConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Group Confirmation' },
                      ],
                      title: 'Group Confirmation',
                    },
                  },
                  // ── URL-embedded flow ID routes ────────────────────────
                  // Every mid-flow step (review, guests) embeds
                  // its cartId (/c/) or groupId (/g/) in the URL so a
                  // browser refresh recovers via useBookingRestore.
                  //
                  // Prefix legend: /c/ = cart-based (single-rate),
                  //                /g/ = group (multi-rate).
                  // Matches the Stripe checkout URL convention where the
                  // resource type is readable from the URL.
                  {
                    path: 'booking/:hotelId/c/:cartId/review',
                    element: withSuspense(BookingConfirmationPage),
                    // The review step. It confirms nothing — Book is on guest
                    // details — so it is not called Confirm Reservation.
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Review' },
                      ],
                      title: 'Review',
                    },
                  },
                  {
                    path: 'booking/:hotelId/c/:cartId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  // Payment was its own step until 2026-10-14; Book is on
                  // guest details now. Old links land there.
                  {
                    path: 'booking/:hotelId/c/:cartId/payment',
                    element: withSuspense(PaymentStepRedirect),
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/review',
                    element: withSuspense(BookingConfirmationPage),
                    // The review step. It confirms nothing — Book is on guest
                    // details — so it is not called Confirm Reservation.
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Review' },
                      ],
                      title: 'Review',
                    },
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  // Payment was its own step until 2026-10-14; Book is on
                  // guest details now. Old links land there.
                  {
                    path: 'booking/:hotelId/g/:groupId/payment',
                    element: withSuspense(PaymentStepRedirect),
                  },
                  {
                    path: 'booking/:hotelId/g/:groupId/confirmation',
                    element: withSuspense(GroupConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Group Confirmation' },
                      ],
                      title: 'Group Confirmation',
                    },
                  },
                ],
              },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
export { routes };
